import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from 'react-hook-form';
import { updateMedicalLeaveFormSchema, type UpdateMedicalLeaveFormValues } from '@/schemas/absence';
import { absenceService } from '@/api/domains/absence/absence.service';
import { ABSENCE_SCOPE_LIST_REFRESH_EVENT } from '@/api/domains/absence/absence.types';
import type {
  LC_MedicalLeaveTypeItemOut,
  LC_AbsenceWorkdayTypeItemOut,
} from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useOverlapErrorBaseline } from '@/hooks/absence/useOverlapErrorBaseline';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { injectValidationErrors } from '@/utils';


const CALCULATED_DATES_DEBOUNCE_MS = 400;
const CONFLICT_DATE_OVERLAP_CODE = 'CONFLICT_DATE_OVERLAP';
const CONFLICT_EMPLOYEE_BLOCKED_CODE = 'CONFLICT_EMPLOYEE_BLOCKED';

export interface UseMedicalLeaveDetailPanelParams {
  medicalLeaveId: number;
  /** Callback opcional adicional al refresco por evento global tras guardar. */
  onSaved?: () => void;
}

export interface UseMedicalLeaveDetailPanelResult {
  form: {
    register: UseFormRegister<UpdateMedicalLeaveFormValues>;
    setValue: UseFormSetValue<UpdateMedicalLeaveFormValues>;
    watch: UseFormWatch<UpdateMedicalLeaveFormValues>;
    errors: FieldErrors<UpdateMedicalLeaveFormValues>;
    trigger: UseFormTrigger<UpdateMedicalLeaveFormValues>;
  };
  employeeName: string;
  /** Administrado desde Remuneraciones — fuerza solo lectura independiente del permiso del
   * usuario. */
  isManagedByPayroll: boolean;
  isReadOnly: boolean;
  loadErrorMessage: string | null;
  isSubmitted: boolean;
  isSubmitting: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
  leaveTypeOptions: SelectorOption[];
  workdayTypeOptions: SelectorOption[];
}

export function useMedicalLeaveDetailPanel({
  medicalLeaveId,
  onSaved,
}: UseMedicalLeaveDetailPanelParams): UseMedicalLeaveDetailPanelResult {
  const { t: translate } = useTranslation('absence');
  const {
    showApiError, showSuccess, showNotification, closeSidePanel, setSidePanelTitleTooltip,
  } = useUI();
  const { setLoading } = useSidePanelLoading();
  const { canEdit } = usePagePermissions();

  // ── Leave types / workday types ──────────────────────────────────────────
  const leaveTypesCatalog = useCatalogLoader<LC_MedicalLeaveTypeItemOut>(
    () => absenceService.getMedicalLeaveTypes(),
    (type) => ({ value: String(type.id), label: type.name }),
    [],
    false,
    () => showNotification('error', translate('medicalLeave.errors.loadLeaveTypesFailed')),
  );
  const workdayTypesCatalog = useCatalogLoader<LC_AbsenceWorkdayTypeItemOut>(
    () => absenceService.getAbsenceWorkdayTypes(),
    (type) => ({ value: type.code, label: type.label }),
    [],
    false,
    () => showNotification('error', translate('medicalLeave.errors.loadWorkdayTypesFailed')),
  );

  // ── Detalle ───────────────────────────────────────────────────────────────
  const [employeeName, setEmployeeName] = useState('');
  const [employeeTargetId, setEmployeeTargetId] = useState<number | null>(null);
  const [isManagedByPayroll, setIsManagedByPayroll] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  // Se marca en éxito y en falla, para que el spinner del panel se apague también cuando la
  // carga falla en vez de esperar el timeout genérico de SidePanelContainer.
  const [isDetailLoadAttempted, setIsDetailLoadAttempted] = useState(false);
  const isDetailLoadedRef = useRef(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    reset,
    handleSubmit: rhfHandleSubmit,
    formState,
  } = useForm<UpdateMedicalLeaveFormValues>({
    resolver: zodResolver(updateMedicalLeaveFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      leaveTypeId: null,
      startDate: '',
      endDate: '',
      folio: '',
      workdayType: '',
      dayCount: null,
      returnDate: '',
    },
  });

  const isReadOnly = !canEdit || isManagedByPayroll;

  useEffect(() => {
    const controller = new AbortController();

    const loadDetail = async () => {
      const response = await absenceService.getMedicalLeaveById(medicalLeaveId, controller.signal);
      if (response.error?.code === 'ABORTED') return;

      if (!response.success || !response.data) {
        const message = showApiError(response, translate('medicalLeave.errors.loadDetailFailed'));
        setLoadErrorMessage(message ?? translate('medicalLeave.errors.loadDetailFailed'));
        setIsDetailLoadAttempted(true);
        return;
      }

      const detail = response.data;
      setEmployeeName(detail.employeeName);
      setEmployeeTargetId(detail.employeeTargetId);
      setIsManagedByPayroll(detail.isManagedByPayroll);
      setSidePanelTitleTooltip(
        detail.isManagedByPayroll
          ? {
            title: translate('medicalLeave.detail.payrollManagedNoticeTitle'),
            body: translate('medicalLeave.detail.payrollManagedNotice'),
          }
          : null,
      );

      reset({
        leaveTypeId: detail.leaveTypeId,
        startDate: detail.startDate,
        endDate: detail.endDate,
        folio: detail.folio,
        workdayType: detail.workdayType,
        dayCount: detail.dayCount,
        returnDate: detail.returnDate,
      });

      isDetailLoadedRef.current = true;
      setIsDetailLoadAttempted(true);
    };

    void loadDetail();

    return () => {
      controller.abort();
      // Limpia el tooltip del título al desmontar para que el próximo panel abierto no lo herede.
      setSidePanelTitleTooltip(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalLeaveId]);

  // ── Spinner del contenedor (una sola pasada) ─────────────────────────────
  const initialLoadDoneRef = useRef(false);
  const isInitialLoadDone = useMemo(
    () => isDetailLoadAttempted && leaveTypesCatalog.isLoaded && workdayTypesCatalog.isLoaded,
    [isDetailLoadAttempted, leaveTypesCatalog.isLoaded, workdayTypesCatalog.isLoaded],
  );

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      setLoading(!isInitialLoadDone);
      if (isInitialLoadDone) {
        initialLoadDoneRef.current = true;
      }
    }
  }, [isInitialLoadDone, setLoading]);

  // ── Fecha de término y fecha de retorno en vivo ────────────────────────────
  // Se calculan en el backend a partir de fecha de inicio + cantidad de días, en una sola llamada
  // (mismo patrón que useCreateVacation.calculateDates).
  const calculatedDatesRequestIdRef = useRef(0);

  const startDateForDates = watch('startDate');
  const dayCountForDates = watch('dayCount');
  const workdayTypeForDates = watch('workdayType');
  const endDateValue = watch('endDate');

  const debouncedStartDateForDates = useDebounce(startDateForDates, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedDayCountForDates = useDebounce(dayCountForDates, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedWorkdayTypeForDates = useDebounce(
    workdayTypeForDates, CALCULATED_DATES_DEBOUNCE_MS,
  );

  const calculateDates = useCallback(
    async (params: {
      employeeTargetId: number;
      startDate: string;
      dayCount: number;
      workdayType: string;
    }) => {
      const requestId = ++calculatedDatesRequestIdRef.current;
      const response = await absenceService.calculateAbsenceDates(params);
      if (requestId !== calculatedDatesRequestIdRef.current) return; // respuesta obsoleta, ignorar
      if (response.success && response.data) {
        setValue('endDate', response.data.endDate, { shouldDirty: true, shouldValidate: true });
        setValue('returnDate', response.data.returnDate, { shouldDirty: true });
      } else {
        // Sin esto, las fechas del cálculo anterior sobreviven al error y la previsualización
        // queda desincronizada de los insumos actuales del formulario.
        setValue('endDate', '');
        setValue('returnDate', '');
        showApiError(response, translate('medicalLeave.errors.calculateDatesFailed'));
      }
    },
    [setValue, showApiError, translate],
  );

  useEffect(() => {
    // Guard obligatorio: antes de que el detalle cargue, el formulario está en blanco y este
    // efecto correría con datos vacíos, borrando las fechas que vinieron del backend.
    if (!isDetailLoadedRef.current) return;
    // En solo lectura no hay motivo para gastar la llamada: nada se va a guardar.
    if (isReadOnly) return;
    const numericDayCount = Number(debouncedDayCountForDates);
    if (
      !employeeTargetId ||
      !debouncedStartDateForDates ||
      !debouncedWorkdayTypeForDates ||
      !Number.isInteger(numericDayCount) ||
      numericDayCount <= 0
    ) {
      return;
    }
    void calculateDates({
      employeeTargetId,
      startDate: debouncedStartDateForDates,
      dayCount: numericDayCount,
      workdayType: debouncedWorkdayTypeForDates,
    });
  }, [
    employeeTargetId,
    debouncedStartDateForDates,
    debouncedDayCountForDates,
    debouncedWorkdayTypeForDates,
    isReadOnly,
    calculateDates,
  ]);

  const { setBaseline: setOverlapErrorBaseline } = useOverlapErrorBaseline(
    startDateForDates, endDateValue, clearErrors,
  );

  // ── Submit ────────────────────────────────────────────────────────────────
  const onValid = useCallback(
    async (data: UpdateMedicalLeaveFormValues) => {
      setIsSubmitting(true);
      try {
        const response = await absenceService.updateMedicalLeave(medicalLeaveId, {
          leaveTypeId: data.leaveTypeId as number,
          startDate: data.startDate,
          folio: data.folio,
          workdayType: data.workdayType,
          dayCount: data.dayCount as number,
        });

        if (response.success) {
          showSuccess(translate('medicalLeave.messages.updateSuccess'));
          window.dispatchEvent(new CustomEvent(ABSENCE_SCOPE_LIST_REFRESH_EVENT));
          closeSidePanel?.();
          onSaved?.();
        } else if (response.error?.code === CONFLICT_DATE_OVERLAP_CODE) {
          setOverlapErrorBaseline(data.startDate, data.endDate);
          setError('startDate', { message: 'absence:medicalLeave.errors.dateOverlap' });
          setError('endDate', { message: 'absence:medicalLeave.errors.dateOverlap' });
          showNotification('error', translate('medicalLeave.errors.dateOverlap'));
        } else if (response.error?.code === CONFLICT_EMPLOYEE_BLOCKED_CODE) {
          // Carrera: el colaborador pasó a sincronizar con Rex+ entre la carga y el guardado.
          setIsManagedByPayroll(true);
          setSidePanelTitleTooltip({
            title: translate('medicalLeave.detail.payrollManagedNoticeTitle'),
            body: translate('medicalLeave.detail.payrollManagedNotice'),
          });
          showNotification('error', translate('medicalLeave.detail.payrollManagedNotice'));
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('medicalLeave.errors.updateFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      medicalLeaveId, onSaved, closeSidePanel, setError, setSidePanelTitleTooltip,
      setOverlapErrorBaseline, showApiError, showNotification, showSuccess, translate,
    ],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (isReadOnly) return;
      const wrappedValid = (data: UpdateMedicalLeaveFormValues) => { void onValid(data); };
      const wrappedInvalid = () => {};
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid, isReadOnly],
  );

  return {
    form: {
      register,
      setValue,
      watch,
      errors: formState.errors,
      trigger,
    },
    employeeName,
    isManagedByPayroll,
    isReadOnly,
    loadErrorMessage,
    isSubmitted: formState.isSubmitted,
    isSubmitting,
    handleSubmit,
    leaveTypeOptions: leaveTypesCatalog.options,
    workdayTypeOptions: workdayTypesCatalog.options,
  };
}

export default useMedicalLeaveDetailPanel;
